import React, { useState } from "react";
import { StyleSheet, TextInput, View, ScrollView } from "react-native";
import { z } from "zod";
import useResponsiveScreen from "../../../hooks/useResponsiveScreen";
import CustomAdminModal from "../../../components/common/general/CustomAdminModal/CustomAdminModal";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import CustomTextInput from "../../../components/common/general/CustomTextInput/CustomTextInput";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import NewCustomAxioActionModal from "../UsersTabContainer/NewCustomAxioActionModal";
import { CreateCoursePlaceholderMutation } from "../../../graphql/generated/graphql";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import AddCourseSecondStep from "./AddCourseSecondStep";
import CurrencyInput from "react-native-currency-input";
import { useAppConfig } from "../../../AppConfigProvider";
import AddCourseDegreesStep from "./AddCourseDegreesStep";

const modals = {
  addConfirmation: {
    title: "Success!",
    ariaLabel: "Course Added Successfully Modal",
    warningText: "The course has been added. You can view the course by clicking the link below.",
    actionButtonText: "OK",
  },
  addError: {
    title: "Error!",
    ariaLabel: "Course Addition Error Modal",
    warningText: "An error occurred while adding the course. You may not have permission to add a course.",
    actionButtonText: "CLOSE",
  },
};

export enum AddCourseModalState {
  CLOSED = "CLOSED",
  ADD_CONFIRMATION = "ADD_CONFIRMATION",
  ADD_ERROR = "ADD_ERROR",
}

const courseSchema = z.object({
  courseName: z.string().min(1, "Course name is required"),
  courseCode: z.string().optional(), // Add course code field
  degrees: z.array(z.object({ id: z.string(), name: z.string() })).optional(),
  description: z.string().optional(),
  objectives: z.string().optional(),
  targetAudience: z.string().optional(),
  cost: z.number().optional(),
});

type CourseFormData = z.infer<typeof courseSchema>;

type SubmitCourseFormData = Omit<CourseFormData, "degrees"> & {
  degrees?: string[];
};

interface AddCourseModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (formData: SubmitCourseFormData) => Promise<CreateCoursePlaceholderMutation>;
}

const AddCourseModal: React.FC<AddCourseModalProps> = ({ visible, onClose, onSubmit }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isMedium } = useResponsiveScreen();
  const [modalState, setModalState] = useState({
    state: AddCourseModalState.CLOSED,
  });
  const { hasDegrees } = useAppConfig();
  const [formStep, setFormStep] = useState<"first" | "hasDegrees" | "second">("first");

  const [activelyEditing, setActivelyEditing] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    getValues,
    reset,
    trigger,
    setValue,
  } = useForm<CourseFormData>({
    resolver: zodResolver(courseSchema),
    defaultValues: {
      courseName: "",
      courseCode: "",
      degrees: [],
      description: "",
      objectives: "",
      targetAudience: "",
      cost: 0,
    },
  });

  const handleClose = () => {
    onClose();
    reset();
    setFormStep("first");
  };

  const handleFormSubmit = React.useCallback(
    async (formData: CourseFormData) => {
      setActivelyEditing(false);
      try {
        setIsSubmitting(true);
        const degrees = formData.degrees.map((d) => d.id);

        // First create the course
        const response = await onSubmit({
          ...formData,
          degrees,
        });
        const result = response?.createCoursePlaceholder;

        if (!result) {
          setModalState({ state: AddCourseModalState.ADD_ERROR });
          setIsSubmitting(false);
          return;
        }

        handleClose();
        if (result.success) {
          setModalState({ state: AddCourseModalState.ADD_CONFIRMATION });
        } else {
          setModalState({ state: AddCourseModalState.ADD_ERROR });
        }
        setIsSubmitting(false);
      } catch (error) {
        console.error("Error submitting form:", error);
        setModalState({ state: AddCourseModalState.ADD_ERROR });
        setIsSubmitting(false);
      }
    },
    [onSubmit, handleClose]
  );

  const isFirstStep = formStep === "first";
  const isSecondStep = formStep === "second";
  const isDegreesStep = formStep === "hasDegrees";

  const handleNext = async () => {
    if (formStep === "first") {
      const isValid = await trigger(["courseName", "courseCode"]); // Add courseCode to validation
      if (isValid) {
        if (hasDegrees) {
          setFormStep("hasDegrees");
        } else {
          setFormStep("second");
        }
      }
    } else if (formStep === "hasDegrees") {
      setFormStep("second");
    }
  };

  const handleBackOrCancel = () => {
    if (formStep === "second") {
      if (hasDegrees) {
        setFormStep("hasDegrees");
      } else {
        setFormStep("first");
      }
    } else if (formStep === "hasDegrees") {
      setFormStep("first");
    } else {
      handleClose();
    }
  };

  return (
    <>
      <CustomAdminModal
        visible={visible}
        onRequestClose={handleClose}
        title="Add a Course"
        ariaLabel="Add course modal"
        style={{ width: isMedium ? "50%" : "90%", maxHeight: "90vh" }}
        onShow={() => {}}>
        <ScrollView style={styles.modalScrollView} showsVerticalScrollIndicator={true} tabIndex={0}>
          <View style={styles.container}>
            {isFirstStep && (
              <>
                <View style={styles.formGroup}>
                  <CustomText
                    text="Course Name"
                    size="s"
                    textType="text"
                    style={{ color: "#182230" }}
                    weight="ultraBold"
                  />
                  <CustomSpacing size="s" type="vertical" />
                  <Controller
                    control={control}
                    name="courseName"
                    render={({ field: { onChange, value } }) => (
                      <CustomTextInput
                        value={value}
                        onChangeText={onChange}
                        placeholder="Understanding Design Change Types"
                        style={styles.input}
                      />
                    )}
                  />
                  {errors?.courseName?.message && (
                    <CustomText size="xs" style={{ color: "#EF4444" }} text={errors.courseName?.message} />
                  )}
                </View>

                <View style={styles.formGroup}>
                  <CustomText
                    text="Course Code (Optional)"
                    size="s"
                    textType="text"
                    style={{ color: "#182230" }}
                    weight="ultraBold"
                  />
                  <CustomText
                    text="Enter a course identifier (e.g., ENG101, MATH201)"
                    size="xs"
                    style={styles.helperText}
                    useTranslationText={false}
                  />
                  <Controller
                    control={control}
                    name="courseCode"
                    render={({ field: { onChange, value } }) => (
                      <CustomTextInput
                        value={value}
                        onChangeText={onChange}
                        placeholder="ENG101"
                        style={styles.input}
                      />
                    )}
                  />
                  {errors?.courseCode?.message && (
                    <CustomText size="xs" style={{ color: "#EF4444" }} text={errors.courseCode?.message} />
                  )}
                </View>

                <View style={styles.formGroup}>
                  <CustomText
                    text="Course Cost"
                    size="s"
                    textType="text"
                    style={{ color: "#182230" }}
                    weight="ultraBold"
                  />
                  <Controller
                    control={control}
                    name="cost"
                    render={({ field: { onChange, value } }) => (
                      <CurrencyInput
                        renderTextInput={(textInputProps) => (
                          <TextInput style={styles.currencyInput} {...textInputProps} />
                        )}
                        value={value}
                        onChangeValue={onChange}
                        delimiter=","
                        separator="."
                        precision={2}
                        minValue={0}
                        keyboardType="numeric"
                        placeholder="Enter amount"
                        prefix="$"
                      />
                    )}
                  />
                  {errors?.cost?.message && (
                    <CustomText size="xs" style={{ color: "#EF4444" }} text={errors.cost?.message} />
                  )}
                </View>
              </>
            )}

            {isDegreesStep && <AddCourseDegreesStep control={control} />}
            {isSecondStep && (
              <AddCourseSecondStep
                control={control}
                setValue={setValue}
                activelyEditing={activelyEditing}
                setActivelyEditing={setActivelyEditing}
              />
            )}

            <View style={styles.buttonContainer}>
              <CustomButton
                text={isFirstStep ? "Cancel" : "Back"}
                onPress={handleBackOrCancel}
                styleType="secondary"
                size="m"
                style={styles.cancelButton}
                textStyle={styles.cancelButtonText}
                leftIcon={null}
                rightIcon={null}
                disabled={false}
                textProps={{}}
              />
              {(isFirstStep || isDegreesStep) && (
                <CustomButton
                  text={"Next"}
                  onPress={handleNext}
                  styleType="primary"
                  size="m"
                  style={styles.addButton}
                  textStyle={styles.addButtonText}
                  leftIcon={null}
                  rightIcon={null}
                  disabled={false}
                  textProps={{}}
                />
              )}
              {isSecondStep && (
                <CustomButton
                  text={"Add Course"}
                  onPress={handleSubmit(handleFormSubmit)}
                  styleType="primary"
                  size="m"
                  style={!isValid || isSubmitting ? { backgroundColor: "#ccc" } : styles.addButton}
                  textStyle={styles.addButtonText}
                  leftIcon={null}
                  rightIcon={null}
                  disabled={!isValid || isSubmitting}
                  textProps={{}}
                />
              )}
            </View>
          </View>
        </ScrollView>
      </CustomAdminModal>
      <NewCustomAxioActionModal
        visible={modalState.state === AddCourseModalState.ADD_CONFIRMATION}
        onClose={() => setModalState({ state: AddCourseModalState.CLOSED })}
        modalTexts={modals.addConfirmation}
        contextualText={getValues("courseName")}
      />
      <NewCustomAxioActionModal
        visible={modalState.state === AddCourseModalState.ADD_ERROR}
        onClose={() => setModalState({ state: AddCourseModalState.CLOSED })}
        modalTexts={modals.addError}
      />
    </>
  );
};

const styles = StyleSheet.create({
  modalScrollView: {
    flex: 1,
    width: "100%",
  },
  container: {
    padding: 24,
  },
  formGroup: {
    marginBottom: 24,
  },
  helperText: {
    color: "#6B7280",
    marginBottom: 8,
  },
  input: {
    width: "100%",
  },
  currencyInput: {
    borderWidth: 1,
    borderColor: "#D1D5DB",
    color: "#1F2937",
    fontSize: 14,
    borderRadius: 8,
    width: "100%",
    padding: 10,
  },
  buttonContainer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    gap: 12,
    marginTop: 8,
  },
  cancelButton: {
    backgroundColor: "transparent",
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 6,
    paddingHorizontal: 16,
  },
  addButton: {
    // backgroundColor: "#3E68FE",
    borderRadius: 6,
    paddingHorizontal: 16,
  },
  cancelButtonText: {
    color: "#4B5563",
  },
  addButtonText: {
    color: "#FFFFFF",
  },
});

export default AddCourseModal;
